/**
 * localStorage can only be used client-side and if the user's permissions do not block it
 *
 * @returns true if localStorage is accessible, false if not
 */
export const canUseLocalStorage = () => {
  if (typeof window !== 'undefined') {
    const test = 'alltrails-local-storage-test';
    try {
      window.localStorage?.setItem(test, test);
      window.localStorage?.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
  return false;
};

export const parseStoredValue = <T>(value: string | null | undefined): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    // JSON.parse can fail for basic strings that were already put into storage before this hook was created
    return JSON.parse(JSON.stringify(value)) as T;
  }
};

export const getItem = <T>(key: string): T | null => {
  if (canUseLocalStorage()) {
    return parseStoredValue<T>(window.localStorage?.getItem?.(key));
  }
  return null;
};

// For values read on every render. Skips canUseLocalStorage, which writes and removes a test key each time: a read alone tells
// blocked storage (it throws) from nothing stored.
export const peekItem = <T>(key: string): T | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return parseStoredValue<T>(window.localStorage?.getItem?.(key));
  } catch {
    return null;
  }
};

export const setItem = <T>(key: string, value: T) => {
  if (canUseLocalStorage()) {
    window.localStorage?.setItem?.(key, JSON.stringify(value));
  }
};

export const removeItem = (key: string) => {
  if (canUseLocalStorage()) {
    window.localStorage?.removeItem?.(key);
  }
};

type ExpiringItem<T> = {
  value: T;
  timestamp: number; // Storing the timestamp instead of the expiration time lets us change the expiration time for existing stored items, if needed
};

// Reads an item written by setItemWithExpiration, returning defaultValue and clearing the key once msUntilExpiration has elapsed.
export const getItemWithExpiration = <T>(key: string, msUntilExpiration: number, defaultValue: T | null = null): T | null => {
  const item = getItem<ExpiringItem<T>>(key);
  if (item) {
    if (Date.now() < item.timestamp + msUntilExpiration) {
      return item.value;
    }
    removeItem(key);
  }
  return defaultValue;
};

// Stamps the value with the current time so getItemWithExpiration can age it out.
export const setItemWithExpiration = <T>(key: string, value: T) => setItem(key, { value, timestamp: Date.now() });
